"use client"

import { useCallback, useEffect, useState } from "react"
import Link from "next/link"
import { IconAlertTriangle, IconCheck, IconChevronRight, IconListCheck } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"
import { toast } from "sonner"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { hasFlag } from "@/lib/feature-flags"
import { completeTask, getTasks, type Task } from "@/lib/tasks-api"
import { cn } from "@/lib/utils"

/**
 * My Tasks — open tasks assigned to (or created by) the current user, due
 * soonest first, overdue highlighted, inline complete. Composed into every
 * role dashboard INCLUDING generic (what simple-mode clinics render). Renders
 * nothing without tasks_enabled + tasks.view.assigned.
 */

/** Never throws — a failed load degrades to the positive empty state. */
async function fetchMyTasks(): Promise<Task[]> {
  try {
    const data = await getTasks({ status: "open", per_page: 6 })
    return data.tasks
  } catch {
    return []
  }
}

export function LegacyMyTasksWidget() {
  const t = useTranslations("tasks.widget")
  const format = useFormatter()
  const { can, isLoaded } = usePermissions()
  const user = useAuthStore((s) => s.user)

  const enabled =
    isLoaded && hasFlag(user?.feature_flags, "tasks_enabled") && can("tasks.view.assigned")

  const [tasks, setTasks] = useState<Task[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(() => {
    fetchMyTasks().then((rows) => {
      setTasks(rows)
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    if (enabled) {
      load()
    }
  }, [enabled, load])

  if (!enabled) {
    return null
  }

  const handleComplete = async (task: Task) => {
    try {
      await completeTask(task.id)
      toast.success(t("completed"))
      load()
    } catch {
      toast.error(t("completeFailed"))
    }
  }

  return (
    <ApolloPanel
      title={t("legacyTitle")}
      actions={
        <Link
          href="/tasks"
          className="inline-flex items-center gap-0.5 text-[12.5px] font-medium text-primary hover:underline"
        >
          {t("viewAll")}
          <IconChevronRight className="size-3.5" />
        </Link>
      }
    >
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 py-6 text-center">
          <IconListCheck className="size-6 text-muted-foreground/50" />
          <p className="text-[12.5px] text-muted-foreground">{t("empty")}</p>
        </div>
      ) : (
        <ul className="divide-y">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[13px] font-medium">{task.title}</span>
                  {task.priority === "high" && (
                    <Badge className="border-0 bg-red-500/10 px-1.5 text-[10px] text-red-600 dark:text-red-400">
                      {t("high")}
                    </Badge>
                  )}
                </div>
                {task.due_at && (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-[11.5px]",
                      task.is_overdue
                        ? "font-medium text-red-600 dark:text-red-400"
                        : "text-muted-foreground"
                    )}
                  >
                    {task.is_overdue && <IconAlertTriangle className="size-3" />}
                    {format.dateTime(new Date(task.due_at), { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-[12px]"
                onClick={() => handleComplete(task)}
              >
                <IconCheck className="size-3.5" />
                {t("done")}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </ApolloPanel>
  )
}
